import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaV1Service } from '../service';
import { FetchCallQueueAnalyticsForMultiTenantIO } from '../types';

export const getFetchCallQueueAnalyticsForMultiTenantQueryKey = (
  req: FetchCallQueueAnalyticsForMultiTenantIO['input']
) => ['V1', 'FetchCallQueueAnalyticsForMultiTenant', req] as const;
type QueryKey = ReturnType<typeof getFetchCallQueueAnalyticsForMultiTenantQueryKey>;

/**
 * A hook that returns a query for the `FetchCallQueueAnalyticsForMultiTenant` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchCallQueueAnalyticsForMultiTenantQuery = <
  E = unknown,
  D = FetchCallQueueAnalyticsForMultiTenantIO['output']
>(
  req: FetchCallQueueAnalyticsForMultiTenantIO['input'],
  options?: LimitedSchemaQueryOptions<FetchCallQueueAnalyticsForMultiTenantIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<FetchCallQueueAnalyticsForMultiTenantIO['output'], E, D, QueryKey>({
    queryKey: getFetchCallQueueAnalyticsForMultiTenantQueryKey(req),
    queryFn: () => SchemaV1Service.FetchCallQueueAnalyticsForMultiTenant(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `FetchCallQueueAnalyticsForMultiTenant` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchCallQueueAnalyticsForMultiTenantQueries = <
  E = unknown,
  D = FetchCallQueueAnalyticsForMultiTenantIO['output']
>(
  argsArr: {
    req: FetchCallQueueAnalyticsForMultiTenantIO['input'];
    options?: LimitedSchemaQueryOptions<FetchCallQueueAnalyticsForMultiTenantIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getFetchCallQueueAnalyticsForMultiTenantQueryKey(req),
      queryFn: () => SchemaV1Service.FetchCallQueueAnalyticsForMultiTenant(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useFetchCallQueueAnalyticsForMultiTenantQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `FetchCallQueueAnalyticsForMultiTenant` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchFetchCallQueueAnalyticsForMultiTenant = () => {
  const queryClient = useQueryClient();

  const fetchFetchCallQueueAnalyticsForMultiTenant = async <
    E = unknown,
    D = FetchCallQueueAnalyticsForMultiTenantIO['output']
  >(
    req: FetchCallQueueAnalyticsForMultiTenantIO['input'],
    options?: LimitedSchemaFetchQueryOptions<FetchCallQueueAnalyticsForMultiTenantIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<FetchCallQueueAnalyticsForMultiTenantIO['output'], E, D, QueryKey>(
      getFetchCallQueueAnalyticsForMultiTenantQueryKey(req),
      () => SchemaV1Service.FetchCallQueueAnalyticsForMultiTenant(req, httpOptions),
      options
    );

    return response;
  };

  return fetchFetchCallQueueAnalyticsForMultiTenant;
};
